import React, {
  MouseEvent,
  useCallback,
  useState,
  useRef,
  useEffect,
} from 'react';
import { ReactEditor, useSlateStatic } from 'slate-react';
import { icons, IconButton, Tooltip, TextField } from '@ublend-npm/aulaui-next';
import { Transforms, Range, Editor, Element, Descendant } from 'slate';
import { hasSelection } from '../../../lib/queries';
import { computePosition, hiddenPosition } from '../../../lib/position';
import { Portal } from '../../Portal/Portal';
import { InputToolbar } from '../../InputWithActions/InputWithActions.styled';
import { inlineLinkType } from '../../../plugins/inlineLink/inlineLinkType';
import { TooltipTitle } from '../../HotkeyLabel/HotkeyLabel.styled';
import { matchesHotkey } from '../../../lib/hotkeys';
import { hotkeys } from '../../../config/hotkeys';
import { HotkeyLabels } from '../../HotkeyLabel/HotkeyLabel';
import { getValidUrl } from '../../../utils/getValidUrl';
import { wrapLink } from '../../../lib/wrapLink';
import { hasLink } from '../../../lib/hasLink';
import { AulaRange } from '../../../types';

interface CachedSelection {
  cachedFragment: Descendant[];
  selection: Range;
}

export const LinkButton = ({
  lastActiveSelection,
  isOpen,
  onOpen: handleOpen,
  onClose: handleClose,
}: {
  lastActiveSelection: AulaRange;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) => {
  const [position, setPosition] = useState(hiddenPosition);
  // we need to store selection and fragment before moving focus to link input
  const [cachedSelection, setCachedSelection] = useState({} as CachedSelection);
  const [showError, setShowError] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputEl = useRef<HTMLInputElement>(null);
  const editor = useSlateStatic();

  const resetComponent = () => {
    setShowError(false);
    handleClose();
    setInputValue('');
  };

  const currentSelectionHasLink = hasLink(editor);

  const cacheAndShowInput = useCallback(() => {
    if (editor.selection !== null) {
      const cachedFragment = Editor.fragment(editor, editor.selection);
      setCachedSelection({
        cachedFragment,
        selection: editor.selection,
      });
    }
    handleOpen();
  }, [editor]);

  const buttonPress = useCallback(
    (event: MouseEvent<HTMLButtonElement> | React.KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      Transforms.select(editor, lastActiveSelection);
      if (hasLink(editor)) {
        Transforms.unwrapNodes(editor, {
          at: editor.selection as Range,
          match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            n.type === inlineLinkType,
        });
        Transforms.collapse(editor, { edge: 'end' });
        ReactEditor.focus(editor);
        return;
      }

      cacheAndShowInput();
    },
    [cacheAndShowInput, editor, lastActiveSelection]
  );

  const onInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        resetComponent();
        // place focus back on the editor
        Transforms.deselect(editor);
        Transforms.select(editor, cachedSelection.selection);
        ReactEditor.focus(editor);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        const urlToSet = getValidUrl(inputValue);

        if (!urlToSet) {
          setShowError(true);
          return;
        }

        wrapLink(editor, urlToSet, cachedSelection.selection);

        resetComponent();
        ReactEditor.focus(editor);
      }
    },
    [cachedSelection, editor, inputValue]
  );

  useEffect(() => {
    const input = inputEl?.current;
    if (isOpen && input) {
      Transforms.select(editor, lastActiveSelection);
      ReactEditor.focus(editor);

      let computedPosition = computePosition(input);
      computedPosition = {
        left: computedPosition.left,
        top: computedPosition.top - 40,
      };
      setPosition(computedPosition);
      // We need to delaying focus otherwise editor scrolls to 0
      setTimeout(() => {
        input.focus();
      }, 100);
    }
  }, [isOpen, editor, lastActiveSelection]);

  useEffect(() => {
    const onkeydown = (e: KeyboardEvent) => {
      const hotkeyCommand = hotkeys.inlineLink.some((hotkey) =>
        matchesHotkey(hotkey, e)
      );
      if (
        hotkeyCommand &&
        hasSelection(editor) &&
        ReactEditor.isFocused(editor)
      ) {
        e.preventDefault();
        cacheAndShowInput();
      }
    };
    document.addEventListener('keydown', onkeydown);
    return () => {
      document.removeEventListener('keydown', onkeydown);
    };
  }, [cacheAndShowInput, editor]);

  return (
    <>
      <Portal togglePortal={resetComponent}>
        {isOpen && (
          <InputToolbar style={position} data-testid="link-input">
            <TextField
              id="link-input-field"
              inputRef={inputEl}
              type="text"
              placeholder=" "
              value={inputValue}
              onChange={(e: any) => {
                if (showError) setShowError(false);
                setInputValue(e.target.value);
              }}
              onKeyDown={onInputKeyDown}
              label="Link"
              error={showError}
              helperText={
                showError ? (
                  <span data-testid="invalid-link">Invalid link</span>
                ) : (
                  <span data-testid="helper-text">
                    Enter to <span className="boldAction">apply changes</span> -
                    Escape to <span className="boldAction">cancel</span>
                  </span>
                )
              }
              onBlur={handleClose}
            />
          </InputToolbar>
        )}
      </Portal>
      <Tooltip
        title={
          <TooltipTitle>
            {currentSelectionHasLink ? 'Remove link' : 'Create link'}
            <HotkeyLabels hotkeys={hotkeys.inlineLink} />
          </TooltipTitle>
        }
      >
        <div>
          <IconButton
            type="secondary"
            size="small"
            selected={currentSelectionHasLink}
            onClick={buttonPress}
            onMouseDown={(e: React.SyntheticEvent) => e.preventDefault()}
            disabled={false}
            label={currentSelectionHasLink ? 'Remove link' : 'Create link'}
            icon={icons.Link}
          />
        </div>
      </Tooltip>
    </>
  );
};
